'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useFormState } from 'react-dom';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { GlobalLoader } from '@app/app/_components/GlobalLoader/GlobalLoader';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import { useHighContrastMode } from '@app/app/_context/AccessibilityContext/useHighContrastMode';
import { ValidationError } from 'yup';
import { buildViolations } from '@app/lib/form/buildViolations';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { ensureString } from '@app/lib/util/ensureString';
import { HomeInsuranceRequestResult, requestHomeInsurance } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { RequestHomeInsurance } from '@app/service/eshop/home/schemas/RequestHomeInsurance';
import { AddressSearchInput } from '@app/app/_components/AddressInput/AddressSearchInput';
import { CountryCode } from '@app/domain/model/CountryCode';
import { Input } from '@app/app/_components/Input/Input';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { Message } from '@app/app/_components/Message/Message';
import { Address } from '@app/domain/model/Address';

interface Props {
    onSuccess?: (result: HomeInsuranceRequestResult) => void;
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    personalCode?: string;
    address?: Address;
    acceptedPrivacyPolicy?: boolean;
    propertyIsEligibleForInsurance?: boolean;
    acceptedToRecieveInfoFromRegisterCenter?: boolean;
}

export function HomeInsuranceLandingSection({
    onSuccess,
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    personalCode,
    address,
    acceptedPrivacyPolicy,
    propertyIsEligibleForInsurance,
    acceptedToRecieveInfoFromRegisterCenter,
}: Props) {
    const t = useTranslations();
    const isMobile = useIsMobile(1024);
    const locale = useLocale();
    const parameters = useAppParameters();
    const isHighContrastMode = useHighContrastMode();
    const [isLoading, setIsLoading] = useState(false);
    const [clientValidationErrors, setClientValidationErrors] = useState<Nullable<ViolationMap>>(null);
    const [isPropertyEligibleForInsurance, setIsPropertyEligibleForInsurance] = useState(false);

    const [formState, formAction] = useFormState(requestHomeInsurance, { status: FormStatus.Pending });

    useEffect(() => {
        if (formState.status === FormStatus.Success && formState.data != null) {
            setIsLoading(false);
            onSuccess?.(formState.data);
        } else if (formState.status === FormStatus.Error) {
            setIsLoading(false);
        }
    }, [formState, onSuccess]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = new FormData(e.currentTarget);

        try {
            await RequestHomeInsurance.validate(
                {
                    personalCode: ensureString(formData.get('personalCode'), ''),
                    country: ensureString(formData.get('country'), ''),
                    city: ensureString(formData.get('city'), ''),
                    municipality: ensureString(formData.get('municipality'), ''),
                    street: ensureString(formData.get('street'), ''),
                    postalCode: ensureString(formData.get('postalCode'), ''),
                    houseNumber: ensureString(formData.get('houseNumber'), ''),
                    apartmentNumber: ensureString(formData.get('apartmentNumber'), ''),
                    acceptPrivacyPolicy: formData.get('acceptPrivacyPolicy') === 'true',
                    isPropertyEligibleForInsurance: formData.get('isPropertyEligibleForInsurance') === 'true',
                    canReceiveInformationFromRegisterCenter:
                        formData.get('canReceiveInformationFromRegisterCenter') === 'true',
                },
                { abortEarly: false },
            );

            setIsLoading(true);
            formAction(formData);
        } catch (error) {
            if (error instanceof ValidationError) {
                const violations = buildViolations(error, 'general.validationError');
                setClientValidationErrors(violations.violations ?? null);
            }
        }
    };

    const handlePropertyEligibilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsPropertyEligibleForInsurance(e.target.checked);
    };

    return (
        <div className="flex min-h-screen flex-col">
            <GlobalLoader isVisible={isLoading} text={t('eshop.home.loadingInformation')} />
            <div className="relative flex flex-col bg-white py-12 lg:px-16">
                {!isMobile && !isHighContrastMode && (
                    <div className="absolute right-0 top-0 h-full max-h-[820px] lg:w-1/2 xl:w-[60%] 2xl:w-[90%]">
                        <Image
                            src={backgroundImageSrc}
                            draggable={false}
                            fill
                            className="object-cover object-center"
                            alt={t('eshop.transport.backgroundImageAlt')}
                        />
                    </div>
                )}
                <form onSubmit={handleSubmit} className="relative z-10 px-4 lg:max-w-[520px]">
                    <Heading className="mb-8 text-4xl 2xl:text-nowrap">{t('eshop.home.landingTitle')}</Heading>

                    <div className="mb-6">
                        <AddressSearchInput
                            defaultCountry={CountryCode.Lithuania}
                            violations={clientValidationErrors ?? formState.violations}
                            defaultValue={address}
                        />
                    </div>

                    <Input
                        name="personalCode"
                        className="mb-6"
                        noSpaces
                        noSymbols
                        label={t('eshop.home.personalCodeLabel')}
                        labelAddon={
                            <InformationPopover content={t('eshop.home.personalCodeTooltip')}>
                                <QuestionCircleIcon className="size-4 text-neutral-500" />
                            </InformationPopover>
                        }
                        placeholder={t('eshop.home.personalCodePlaceholder')}
                        error={clientValidationErrors?.personalCode ?? formState.violations?.personalCode}
                        defaultValue={personalCode}
                    />

                    <div className="mb-10 flex flex-col gap-6">
                        <CheckboxInput
                            defaultChecked={propertyIsEligibleForInsurance}
                            name="isPropertyEligibleForInsurance"
                            label={t.rich('eshop.home.isPropertyEligibleForInsuranceLabel', {
                                li: chunks => <li className="underline">{chunks}</li>,
                            })}
                            error={formState.violations?.isPropertyEligibleForInsurance}
                            onChange={handlePropertyEligibilityChange}
                        />

                        {clientValidationErrors?.isPropertyEligibleForInsurance && !isPropertyEligibleForInsurance && (
                            <Message
                                variant="warning"
                                containerClassName="items-center"
                                addon={
                                    <Button
                                        variant="outlined"
                                        preventDefault
                                        size="tiny"
                                        className="w-max"
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopHomeInsuranceInfoUrls.contactUs
                                                : parameters.eshopHomeInsuranceInfoUrls.contactUsEn
                                        }
                                        rightAddon={<ArrowRightIcon className="size-4" />}>
                                        {t('general.contactUs')}
                                    </Button>
                                }>
                                {t('eshop.home.cannotPurchaseInsuranceOnline')}
                            </Message>
                        )}

                        <CheckboxInput
                            defaultChecked={acceptedToRecieveInfoFromRegisterCenter}
                            name="canReceiveInformationFromRegisterCenter"
                            label={t('eshop.home.registerCenterLabel')}
                            error={
                                clientValidationErrors?.canReceiveInformationFromRegisterCenter ??
                                formState.violations?.canReceiveInformationFromRegisterCenter
                            }
                        />

                        <CheckboxInput
                            name="acceptPrivacyPolicy"
                            defaultChecked={acceptedPrivacyPolicy}
                            label={t.rich('eshop.transport.acceptPrivacyPolicyLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.privacyPolicy
                                                : parameters.eshopInfoUrls.privacyPolicyEn
                                        }>
                                        {chunks}
                                    </Link>
                                ),
                            })}
                            error={
                                clientValidationErrors?.acceptPrivacyPolicy ?? formState.violations?.acceptPrivacyPolicy
                            }
                        />
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        <Button rightAddon={<ArrowRightIcon className="size-4" />}>{t('general.next')}</Button>
                        <Button
                            href={parameters.eshopHomeInsuranceInfoUrls.readMoreInfo}
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}
                            variant="outlined">
                            {t('eshop.general.readMore')}
                        </Button>
                    </div>
                </form>
            </div>
            <div className="relative h-[600px] w-full lg:hidden">
                <Image
                    src={mobileImageSrc}
                    fill
                    className="object-cover"
                    alt={t('eshop.transport.backgroundImageAlt')}
                />
            </div>

            <EsalesTransportFooter
                className="lg:mt-[500px]"
                stakeholderLogoSrc={stakeholderLogoSrc}
                vigLogoSrc={vigLogoSrc}
            />
        </div>
    );
}
